/**
 * A tool call in the transcript. Plan-mode tools and questions render their
 * own card; everything else is one `ToolRow` line (Cursor's "Read I-129.pdf")
 * followed only by what needs a person or a click: the approval line, the
 * result list for entities the call returned, a background run's progress
 * line, a handoff card.
 */
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Clock,
  Download,
  ExternalLink,
  Loader2,
  MessageSquare,
  MinusCircle,
  Pencil,
  X
} from "lucide-react";
import {
  memo,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode
} from "react";
import { Link } from "react-router-dom";

import {
  Confirmation,
  ConfirmationAccepted,
  ConfirmationAction,
  ConfirmationActions,
  ConfirmationRejected,
  ConfirmationRequest,
  ConfirmationTitle
} from "@/components/ai-elements/confirmation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToolResultList } from "@/features/agents/components/ToolResultList";
import { useOpenEntity } from "@/features/agents/hooks/useOpenEntity";
import {
  entitiesFromToolOutput,
  type ToolResultEntity
} from "@/features/agents/refs/toolEntities";
import type {
  AgentChange,
  ApprovalDecision,
  ApprovalReview,
  HandoffResult
} from "@/features/agents/types";
import type { StepData } from "@/features/agents/useAgentChat";
import {
  PlaybookSuggestionCard,
  playbookSuggestionOf
} from "@/features/companies/components/PlaybookSuggestionCard";
import { ConnectAppsCard } from "@/features/connectors/components/ConnectAppsCard";
import { firmKnowledgePath } from "@/features/firmKnowledge/routes";
import { FirmSetupCard } from "@/features/onboarding/components/FirmSetupCard";
import { PrefillCard } from "@/features/onboarding/components/PrefillCard";
import { SuggestedAgentsCard } from "@/features/onboarding/components/SuggestedAgentsCard";
import {
  TimeEntryCard,
  timeEntryChangesOf
} from "@/features/timeTracking/components/TimeEntryCard";
import { cn } from "@/lib/utils";

import type { ConnectAppsOutput } from "@/features/connectors/types";
import { useOfferActionsContext } from "../offers/OfferActionsContext";
import { triggerSignedUrlDownload } from "../../../../api/utils/filingMaterialsExport";

import { useCaseIdeOptional } from "../../context/CaseIdeContext";
import { useCaseDataOptional } from "../../data/CaseDataContext";
import type { ArtifactKind, AskUserAnswers } from "../../types";
import { useRunEvent } from "../runEvents";
import {
  autocategorizeInFlight,
  runFromToolOutput,
  runView,
  withRunEvent,
  type RunKind,
  type RunView
} from "../runTracking";
import { runMinutes } from "../../editor/DraftRunBanner";
import { useAutocategorizeRun } from "../useAutocategorizeRun";
import { OrganizerPlanCard } from "./OrganizerPlanCard";
import { OrganizerRunMoves } from "./OrganizerRunMoves";
import { useExportRun } from "../useExportRun";
import { useTitleFor } from "../useTitleFor";

import { ChangeDetails, UndoControl, useChangeUndo } from "./ChangeCard";
import { AskAgentCard, DelegationStep, HandoffCard } from "./HandoffCard";
import { FirmSettingsApprovalPreview } from "./FirmSettingsApprovalPreview";
import { IntakeApprovalPreview } from "./IntakeApprovalPreview";
import { useApprovalReview, useToolChange } from "./messageParts";
import { PlanCard } from "./PlanCard";
import { OfferCard } from "./OfferCard";
import { QuestionCard } from "./QuestionCard";
import { PdfDraftCard, pdfBatchIdOf } from "./PdfDraftCard";
import {
  ReadNote,
  emptyListNote,
  readDocumentOutputOf,
  readNoteText
} from "./ReadNote";
import { RunFailureCard, runFailureOf } from "./RunFailureCard";
import { SupportHandoffCard } from "./SupportHandoffCard";
import { TodoListCard } from "./TodoListCard";
import {
  ActivityLine,
  CallDetails,
  outputOfPart,
  StepRows,
  ToolRow,
  useToolTitle
} from "./ToolRow";
import {
  appName,
  appToolLabel,
  browserTaskIsRead,
  browserTaskText,
  isBrowserTaskSlug,
  OWN_CARD_TOOLS,
  countedTitle,
  pdfOpsSummary,
  presentTitle,
  showRawCallDetails,
  toolKind
} from "./toolTitles";
import { WaitingLine } from "./WaitingOnYou";

export type AnyToolPart = ToolUIPart | DynamicToolUIPart;

type Props = {
  part: AnyToolPart;
  onApproval: (decision: ApprovalDecision) => void;
  onAnswer?: (toolCallId: string, answers: AskUserAnswers) => void;
  onConnectResolved?: (toolCallId: string, output: ConnectAppsOutput) => void;
  /**
   * Live nested steps for a call that delegated to another agent (from
   * `data-step`): a `task` subagent's, or the drafting / research / organizer
   * run a `generate_draft` / `start_research` / `start_autocategorize` call
   * is waiting on.
   */
  steps?: readonly StepData[];
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/**
 * An approval card's question and, under it, what happens if approved.
 * `saysNoUndo`: the detail already says it cannot be undone, so the review's
 * own "cannot be undone" line would repeat it. The review is not always sent,
 * so the detail keeps the warning.
 */
type ApprovalCopy = { title: string; detail?: string; saysNoUndo?: boolean };

const trimmedString = (input: unknown, key: string): string | undefined => {
  const value = isRecord(input) ? input[key] : undefined;
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

const joinDetail = (...parts: (string | undefined)[]): string | undefined =>
  parts.filter(Boolean).join(" ") || undefined;

const modeSwitchCopy = (input: unknown): ApprovalCopy => {
  const mode =
    isRecord(input) && typeof input.mode === "string" ? input.mode : null;
  const reason = trimmedString(input, "reason");
  if (mode === "plan")
    return {
      title: "Switch to Plan mode?",
      detail: joinDetail(
        "The agent will only read and plan until you switch back.",
        reason
      )
    };
  if (mode === "agent")
    return {
      title: "Switch to Agent mode?",
      detail: joinDetail("The agent will start acting on the case.", reason)
    };
  return { title: "Switch modes?", detail: reason };
};

/**
 * Browser's create-task tool can look or edit. An inspect does not claim a
 * data change, and the card never shows the tool's internal name.
 */
const browserTaskApprovalCopy = (
  slugs: string[],
  input: unknown,
  purpose: string | undefined
): ApprovalCopy | null => {
  if (!slugs.length || !slugs.every(isBrowserTaskSlug)) return null;
  if (browserTaskIsRead(browserTaskText(input)))
    return { title: "Let Casey look in Browser?", detail: purpose };
  return {
    title: "Let Casey run this in Browser?",
    detail: joinDetail("This changes data in your Browser account.", purpose)
  };
};

/**
 * `run_app_tools` pauses when a step changes something in a connected app:
 * name the app and the action so the attorney knows what they are allowing.
 */
const appToolsApprovalCopy = (input: unknown): ApprovalCopy => {
  const tools =
    isRecord(input) && Array.isArray(input.tools) ? input.tools : [];
  const slugs = tools
    .map((t) =>
      isRecord(t) && typeof t.tool_slug === "string" ? t.tool_slug : null
    )
    .filter((s): s is string => Boolean(s));
  const purpose = trimmedString(input, "purpose");
  if (!slugs.length)
    return { title: "Let Casey run these app tools?", detail: purpose };
  const browser = browserTaskApprovalCopy(slugs, input, purpose);
  if (browser) return browser;
  const labels = slugs.map(appToolLabel);
  const apps = [...new Set(slugs.map((s) => appName(s.split("_")[0])))];
  const what =
    labels.length === 1
      ? labels[0]
      : `${labels[0]} and ${labels.length - 1} more`;
  return {
    title: `Let Casey run ${what}?`,
    detail: joinDetail(
      `This changes data in your ${apps.join(" and ")} account.`,
      purpose
    )
  };
};

/** `resolve_draft_changes` resolves every pending change, by anyone. */
export const resolveChangesCopy = (input: unknown): ApprovalCopy => {
  const verb =
    isRecord(input) && input.action === "accept_all" ? "Accept" : "Reject";
  return {
    title: `${verb} every tracked change in this draft, including teammates'?`,
    detail: "The current version stays in the draft's history."
  };
};

/** `edit_pdf` ops that move, remove or add pages (citations follow them). */
const PAGE_STRUCTURE_OPS = new Set([
  "delete_pages",
  "keep_pages",
  "insert_from_node"
]);

const CITATIONS_REPOINTED =
  "Citations in drafts to the pages that move are re-pointed to match; Undo puts them back too.";

/**
 * `edit_pdf` asks only for what removes or rewrites what is on the page
 * (redaction, stamps, watermarks, removing marks, page changes): say what the
 * ops do and where, from the ops themselves.
 */
export const editPdfApprovalCopy = (input: unknown): ApprovalCopy => {
  const target = trimmedString(input, "target");
  const where = target?.toLowerCase() === "packet" ? "the packet" : "this file";
  if (trimmedString(input, "batch_id"))
    return {
      title: `Apply the checked edits to ${where}?`,
      detail:
        "These are the changes listed when the edit was checked. Undo restores the previous version."
    };
  const ops = isRecord(input) && Array.isArray(input.ops) ? input.ops : [];
  const summary = pdfOpsSummary(ops) ?? "Edit";
  const redacts = ops.some((op) => isRecord(op) && op.op === "redact");
  const movesPages = ops.some(
    (op) => isRecord(op) && PAGE_STRUCTURE_OPS.has(String(op.op))
  );
  return {
    title: `${summary} on ${where}?`,
    detail: joinDetail(
      redacts
        ? "Redacted text is removed from the new version, not covered."
        : undefined,
      movesPages && where === "this file" ? CITATIONS_REPOINTED : undefined,
      where === "the packet"
        ? "Each change lands on the exhibit file under that page (tab pages keep theirs across rebuilds). Undo restores the previous versions."
        : "Undo restores the previous version, which also stays in the file's history."
    )
  };
};

const APPROVAL_COPY: Record<string, ApprovalCopy> = {
  delete_nodes: {
    title: "Delete these files?",
    detail: "This removes them from the case and renumbers the exhibits."
  },
  delete_draft: {
    title: "Delete this draft?",
    detail: "This cannot be undone.",
    saysNoUndo: true
  },
  apply_autocategorize_plan: {
    title: "Apply the organizer's plan?",
    detail: "Files will be moved into the proposed folders."
  },
  update_case_status: { title: "Change the case status?" },
  update_case_deadline: {
    title: "Change the filing deadline?",
    detail: "A later or missing deadline can let a filing slip."
  },
  set_exhibit_scheme: {
    title: "Change the exhibit numbering scheme for the whole case?"
  },
  create_reminder: { title: "Add this reminder to the client's calendar?" },
  create_client_reminder: {
    title: "Add this reminder to the client's calendar?"
  },
  create_client_reminders: {
    title: "Add this reminder to every client listed?",
    detail: "One reminder each, on their own calendars."
  },
  complete_reminder: { title: "Mark this reminder as done?" },
  update_client_profile: {
    title: "Update the client's profile with these changes?"
  },
  set_client_photo: {
    title: "Replace the client's profile photo with this image?"
  },
  propose_client_note: {
    title: "Add this to the client's notes?",
    detail: "The assistant follows the notes on every turn about this client."
  },
  update_firm_skill: {
    title: "Change this enabled automation?",
    detail: "Your agents follow the new version from their next run."
  },
  open_case: {
    title: "Open a new case for this client?",
    detail:
      "It appears in the firm's cases right away and can be deleted from the case if opened by mistake."
  },
  add_client_record: { title: "Add this entry to the client's record?" },
  update_client_record: { title: "Update this entry in the client's record?" },
  delete_client_record: {
    title: "Remove this entry from the client's record?",
    detail: "This cannot be undone.",
    saysNoUndo: true
  },
  send_documents_to_case: {
    title: "Send these documents to the case?",
    detail:
      "Copies are added to the case files; the originals stay in the vault."
  },
  delete_client_document: {
    title: "Delete this document?",
    detail:
      "The file is removed from the vault; copies already sent to a case stay there."
  },
  send_intake: {
    title: "Email this intake to the client?",
    detail: "They receive a link to fill it in."
  },
  remind_intake: {
    title: "Send the client a reminder about this intake?",
    detail: "They receive a fresh link by email."
  },
  request_intake_revision: {
    title: "Ask the client to revise their answers?",
    detail: "They get the intake back by email with your note."
  },
  handoff_to_agent: {
    title: "Hand this work to another agent?",
    detail: "It will run in that agent's thread, where the team can follow it."
  },
  start_pdf_edit: {
    title: "Edit this PDF?",
    detail:
      "A file gets a new version you can restore. On the packet, marks and text land on the exhibits and tab pages and survive rebuilds; page changes to the packet copy are lost on the next rebuild."
  },
  edit_pdf_pages: {
    title: "Change this file's pages?",
    detail: `${CITATIONS_REPOINTED} The previous version stays in history.`
  },
  redact_pdf: {
    title: "Redact this file?",
    detail:
      "The text under each redaction is removed for good in the new version; the previous version stays in history."
  },
  stamp_pdf: {
    title: "Stamp this file?",
    detail:
      "Page numbers, Bates labels or a watermark are added to a new version; the previous version stays in history."
  },
  remove_pdf_annotations: {
    title: "Remove these annotations from the file?",
    detail: "The previous version stays in history."
  }
};

const FIRM_SECTION_TITLES: Record<string, string> = {
  details: "Update the firm's details?",
  instructions: "Save these firm instructions?",
  house_style: "Change the firm's exhibit house style?",
  case_preparation: "Change the firm's case preparation defaults?",
  approvals: "Change how agents ask for approval at the firm?",
  time_billing: "Change time and billing for the firm?",
  permissions: "Change who can edit the firm's library and automations?"
};

/** Firm-wide settings always ask, whoever is signed in. */
export const firmSettingsApprovalCopy = (input: unknown): ApprovalCopy => {
  const section = trimmedString(input, "section") ?? "";
  const patch = isRecord(input) && isRecord(input.patch) ? input.patch : {};
  if (!Object.keys(patch).length && section !== "instructions")
    return {
      title: "Confirm these firm settings as they are?",
      detail: "Nothing changes; the item is marked reviewed for the firm."
    };
  return {
    title: FIRM_SECTION_TITLES[section] ?? "Change the firm's settings?",
    detail:
      section === "instructions"
        ? "Every agent at the firm reads these first. You can undo this afterwards."
        : "This applies to everyone at the firm. You can undo this afterwards."
  };
};

/**
 * The card's question. The tool row above already names the step, so a tool
 * without written wording only needs to ask.
 */
const approvalCopy = (name: string, input: unknown): ApprovalCopy => {
  if (name === "request_mode_switch") return modeSwitchCopy(input);
  if (name === "run_app_tools") return appToolsApprovalCopy(input);
  if (name === "resolve_draft_changes") return resolveChangesCopy(input);
  if (name === "edit_pdf") return editPdfApprovalCopy(input);
  if (name === "save_firm_settings") return firmSettingsApprovalCopy(input);
  return APPROVAL_COPY[name] ?? { title: "Approve this step?" };
};

export const toolNameOf = (part: AnyToolPart): string =>
  part.type === "dynamic-tool"
    ? part.toolName
    : part.type.replace(/^tool-/, "");

/**
 * Whether the card layout may fold this call into an "Explored N files" line:
 * a read-only call that needs nobody. Writes, background runs, questions and
 * anything awaiting approval stay on their own line.
 */
export const isFoldableRead = (part: AnyToolPart): boolean => {
  const name = toolNameOf(part);
  if (toolKind(name) !== "read") return false;
  return !("approval" in part && part.approval);
};

/** The artifact the call acted on, when the output names one. */
const openTargetOf = (
  output: unknown
): { kind: ArtifactKind; id: string; title?: string } | null => {
  if (!isRecord(output)) return null;
  const o = output.open;
  if (isRecord(o) && typeof o.kind === "string" && o.id != null) {
    return {
      kind: o.kind as ArtifactKind,
      id: String(o.id),
      title: typeof o.title === "string" ? o.title : undefined
    };
  }
  return null;
};

const errorOf = (part: AnyToolPart): string | undefined => {
  if (part.state === "output-error") return part.errorText;
  if (
    part.state === "output-available" &&
    isRecord(part.output) &&
    typeof part.output.error === "string"
  ) {
    return part.output.error;
  }
  return undefined;
};

const EMPTY_SECTIONS: Parameters<typeof runView>[1] = [];

/** `handoff_to_agent` output → the card's props (null when not a handoff). */
export const handoffFromToolOutput = (
  output: unknown
): HandoffResult | null => {
  if (!isRecord(output) || typeof output.thread_id !== "string") return null;
  if (typeof output.agent !== "string" || typeof output.link !== "string")
    return null;
  return output as unknown as HandoffResult;
};

const RUN_KIND_TO_ARTIFACT: Partial<Record<RunKind, ArtifactKind>> = {
  draft: "draft",
  research: "draft",
  packet: "packet",
  form: "form"
};

/** The plan-mode tools have purpose-built cards instead of the generic row. */
const OwnCard: FC<{
  name: string;
  part: AnyToolPart;
  onAnswer?: (toolCallId: string, answers: AskUserAnswers) => void;
  onConnectResolved?: (toolCallId: string, output: ConnectAppsOutput) => void;
}> = ({ name, part, onAnswer, onConnectResolved }) => {
  const title = useToolTitle(name, part.input, outputOfPart(part));
  const offers = useOfferActionsContext();
  const error = errorOf(part);
  if (error) {
    return (
      <ActivityLine
        icon={title.icon}
        status="error"
        title={presentTitle(name, part.input)}
        data={{ tool: name }}
      >
        <pre className="max-h-40 overflow-auto rounded-md bg-danger-soft p-2 font-mono text-xs leading-snug whitespace-pre-wrap break-words text-destructive">
          {error}
        </pre>
      </ActivityLine>
    );
  }
  switch (name) {
    case "ask_user":
      // The open question is docked on the composer (CaseChatPanel); the
      // transcript only keeps the recorded answers.
      if (part.state !== "output-available") return null;
      return (
        <QuestionCard part={part} onAnswer={onAnswer ?? (() => undefined)} />
      );
    case "create_plan":
    case "update_plan":
      return <PlanCard part={part} />;
    case "update_todos":
      return <TodoListCard part={part} />;
    case "ask_agent":
      return <AskAgentCard part={part} />;
    case "offer_action":
      if (part.state !== "output-available") return null;
      return <OfferCard part={part} />;
    case "contact_support":
      if (part.state !== "output-available") return null;
      return <SupportHandoffCard part={part} />;
    case "connect_apps": {
      // The turn is paused on this card; it resolves itself once the apps
      // are connected (or the attorney skips).
      const done = part.state === "output-available";
      const skipped =
        done && isRecord(part.output) && Boolean(part.output.skipped);
      return (
        <div className="flex min-w-0 flex-col gap-1">
          <ActivityLine
            icon={title.icon}
            status={done ? "done" : "running"}
            title={
              skipped
                ? `Skipped connecting ${title.target ?? "an app"}`
                : done
                ? title.past
                : title.present
            }
            data={{ tool: name }}
          />
          <ConnectAppsCard part={part} onResolve={onConnectResolved} />
        </div>
      );
    }
    case "show_suggested_agents": {
      const done = part.state === "output-available";
      return (
        <div className="flex min-w-0 flex-col gap-1">
          <ActivityLine
            icon={title.icon}
            status={done ? "done" : "running"}
            title={done ? title.past : title.present}
            data={{ tool: name }}
          />
          <SuggestedAgentsCard part={part} onResolve={offers?.resolveAgents} />
        </div>
      );
    }
    case "show_firm_setup":
    case "prefill_from_connected_apps": {
      const done = part.state === "output-available";
      return (
        <div className="flex min-w-0 flex-col gap-1">
          <ActivityLine
            icon={title.icon}
            status={done ? "done" : "running"}
            title={done ? title.past : title.present}
            data={{ tool: name }}
          />
          {name === "show_firm_setup" ? (
            <FirmSetupCard part={part} />
          ) : (
            <PrefillCard part={part} />
          )}
        </div>
      );
    }
    default:
      return null;
  }
};

/** "Writing Criterion 3 · 4 min" while a draft's run goes, else "in progress". */
const useRunningNote = (view: RunView): string => {
  const since = view.status === "running" ? view.since ?? null : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (since === null) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [since]);
  const parts = [view.activity || "in progress"];
  if (since !== null) parts.push(`${runMinutes(since, now)} min`);
  return parts.join(" · ");
};

/** A background run the call started, as one line with its state and actions. */
const RunLine: FC<{
  view: RunView;
  onOpen?: (() => void) | null;
  downloadUrl?: string | null;
}> = ({ view, onOpen, downloadUrl }) => {
  const running = useRunningNote(view);
  return (
    <div
      className="flex h-6 min-w-0 items-center gap-1.5 pr-1 text-sm text-muted-foreground"
      data-testid="run-line"
      data-run-status={view.status}
    >
      <span className="flex size-3.5 shrink-0 items-center justify-center">
        {view.status === "running" ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : view.status === "failed" ? (
          <AlertTriangle className="size-3.5 text-destructive" />
        ) : view.status === "stale" ? (
          <CircleDot className="size-3.5 text-warn" />
        ) : view.status === "waiting" ? (
          <Clock className="size-3.5 text-fg-3" />
        ) : view.status === "idle" ? (
          <MinusCircle className="size-3.5 text-muted-foreground" />
        ) : (
          <CheckCircle2 className="size-3.5 text-success" />
        )}
      </span>
      <span
        className="min-w-0 flex-1 truncate"
        title={view.detail ?? undefined}
      >
        <span className="text-fg">{view.title}</span>
        <span className="ml-1 text-fg-3">
          {view.status === "running"
            ? running
            : view.status === "failed"
            ? "failed"
            : view.status === "stale"
            ? view.run.kind === "autocategorize"
              ? "plan ready, review below"
              : "out of date"
            : view.status === "waiting"
            ? "waiting for analysis"
            : view.status === "idle"
            ? "canceled"
            : "done"}
        </span>
      </span>
      {onOpen && view.status !== "running" ? (
        <Button
          variant="ghost"
          size="xs"
          className="h-5 gap-1 px-1.5 text-xs"
          onClick={onOpen}
        >
          Open <ExternalLink className="size-3" />
        </Button>
      ) : null}
      {downloadUrl ? (
        <Button
          variant="ghost"
          size="xs"
          className="h-5 gap-1 px-1.5 text-xs"
          onClick={() => triggerSignedUrlDownload(downloadUrl)}
        >
          Download <Download className="size-3" />
        </Button>
      ) : null}
    </div>
  );
};

const TARGET_NOUN: Record<string, string> = {
  case: "Case",
  client: "Client",
  practice: "Practice"
};

const BODY_KEYS = ["subject", "body", "message", "text", "content", "html"];

/** What an outbound call will send, read off its arguments. */
export const outboundBodyOf = (input: unknown): string | null => {
  if (!isRecord(input)) return null;
  const lines = BODY_KEYS.filter(
    (k) => typeof input[k] === "string" && input[k]
  ).map((k) => (k === "subject" ? `Subject: ${input[k]}` : String(input[k])));
  if (lines.length) return lines.join("\n\n");
  return JSON.stringify(input, null, 2);
};

/** How many of a bulk call's clients the card names before "and N more". */
const MAX_LISTED_TARGETS = 12;

/** `"Name <email>"` → the name and the email apart; anything else is the name. */
export const splitRecipient = (
  recipient: string
): { name: string; email: string | null } => {
  const match = /^(.*?)\s*<([^>]+)>\s*$/.exec(recipient.trim());
  if (match && match[1]) return { name: match[1], email: match[2] };
  return { name: recipient.trim(), email: null };
};

const FactRow: FC<{ label: string; children: ReactNode }> = ({
  label,
  children
}) => (
  <div className="flex items-baseline gap-1.5">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5 text-foreground">
      {children}
    </div>
  </div>
);

/**
 * What the server's review adds to an approval card: who receives an outbound
 * message, where the write lands (named when it is not the turn's focus, and
 * called "not this thread's" only when the thread's home is that kind of
 * record), every client a bulk call reaches, whether it could be undone, the
 * intake an email links to, and what will be sent when the turn read outside
 * content.
 */
const ApprovalFacts: FC<{
  review: ApprovalReview;
  input: unknown;
  showBody: boolean;
  onToggleBody: () => void;
  saysNoUndo?: boolean;
}> = ({ review, input, showBody, onToggleBody, saysNoUndo }) => {
  const { target } = review;
  const targetLabel = target
    ? target.label || `${TARGET_NOUN[target.kind] ?? target.kind} ${target.id}`
    : null;
  const targets = review.targets ?? [];
  const listed = targets
    .slice(0, MAX_LISTED_TARGETS)
    .map((t) => t.label || `${TARGET_NOUN[t.kind] ?? t.kind} ${t.id}`);
  const more = targets.length - listed.length;
  const recipient = review.recipient ? splitRecipient(review.recipient) : null;
  const tainted = review.tainted_sources?.length ?? 0;
  const irreversible =
    review.reversible === false && !review.outbound && !saysNoUndo;
  const intakePreview =
    review.preview?.kind === "intake" ? review.preview : null;
  const firmPreview =
    review.preview?.kind === "firm_settings" ? review.preview : null;
  const body =
    review.show_body && !intakePreview && !firmPreview
      ? outboundBodyOf(input)
      : null;
  if (
    !targetLabel &&
    !listed.length &&
    !recipient &&
    !tainted &&
    !irreversible &&
    !body &&
    !intakePreview &&
    !firmPreview
  )
    return null;
  return (
    <div className="flex flex-col gap-1.5 text-xs" data-testid="approval-facts">
      {recipient ? (
        <FactRow label="To">
          <span className="font-medium">{recipient.name}</span>
          {recipient.email ? (
            <span className="min-w-0 break-all text-muted-foreground">
              {recipient.email}
            </span>
          ) : null}
        </FactRow>
      ) : null}
      {targetLabel && target ? (
        <FactRow label="Where">
          <span className="font-medium">{targetLabel}</span>
          {review.home_kind === target.kind ? (
            <span className="text-muted-foreground">
              not this thread&apos;s {target.kind}
            </span>
          ) : null}
        </FactRow>
      ) : null}
      {listed.length ? (
        <FactRow label="For">
          <span>
            <span className="font-medium">{listed.join(", ")}</span>
            {more > 0 ? (
              <span className="text-muted-foreground">, and {more} more</span>
            ) : null}
          </span>
        </FactRow>
      ) : null}
      {irreversible ? (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <AlertTriangle className="size-3 shrink-0" />
          This cannot be undone afterwards.
        </div>
      ) : null}
      {tainted ? (
        <div className="flex items-start gap-1.5 rounded-md bg-fill-1 px-2 py-1.5 text-foreground">
          <AlertTriangle className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
          <span>
            Built from content the agent read in {tainted} document
            {tainted === 1 ? "" : "s"} or message{tainted === 1 ? "" : "s"};
            check it says what you mean.
          </span>
        </div>
      ) : null}
      {firmPreview ? (
        <FirmSettingsApprovalPreview preview={firmPreview} />
      ) : null}
      {intakePreview ? (
        <IntakeApprovalPreview
          preview={intakePreview}
          input={input}
          clientName={recipient?.name ?? null}
        />
      ) : null}
      {body ? (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            className="inline-flex items-center gap-1 self-start text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={onToggleBody}
            data-testid="approval-show-body"
          >
            {showBody ? (
              <ChevronDown className="size-3" />
            ) : (
              <ChevronRight className="size-3" />
            )}
            {showBody ? "Hide what will be sent" : "Show what will be sent"}
          </button>
          {showBody ? (
            <div className="max-h-45 overflow-auto whitespace-pre-wrap rounded-md bg-fill-1 px-2.5 py-2 text-xs text-foreground">
              {body}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * A recorded write as its row: whose record it changed and Undo at the end,
 * what else it updated underneath. In a narrow panel the name goes first,
 * then Undo shrinks to its icon.
 */
const ChangeToolRow: FC<{
  part: AnyToolPart;
  change: AgentChange;
  detail?: ReactNode;
  defaultOpen?: boolean;
  /** More about what the write did; gone once it is undone. */
  footer?: ReactNode;
}> = ({ part, change: initial, detail, defaultOpen, footer }) => {
  const state = useChangeUndo(initial);
  const { change, undone, where } = state;
  return (
    <div
      className="@container flex min-w-0 flex-col gap-0.5"
      data-testid="change-row"
      data-change-id={change.id}
      data-status={change.status}
    >
      <ToolRow
        part={part}
        detail={detail}
        defaultOpen={defaultOpen}
        struck={undone}
        trailing={
          <>
            {where ? (
              <span
                className="max-w-40 truncate text-sm text-fg-4 @max-sm:hidden"
                data-testid="change-target"
              >
                {where}
              </span>
            ) : null}
            <UndoControl state={state} />
          </>
        }
      />
      <ChangeDetails state={state} className="pl-5" />
      {footer && !undone ? <div className="pl-5">{footer}</div> : null}
    </div>
  );
};

/** A call whose one result the row already names: the title opens it. */
const LinkedToolRow: FC<{
  part: AnyToolPart;
  entity: ToolResultEntity;
  detail?: ReactNode;
  defaultOpen?: boolean;
}> = ({ part, entity, detail, defaultOpen }) => {
  const { open, previews } = useOpenEntity();
  return (
    <ToolRow
      part={part}
      detail={detail}
      defaultOpen={defaultOpen}
      onOpenTarget={() => open(entity)}
      targetInPanel={previews(entity)}
    />
  );
};

const GenericToolCard: FC<Omit<Props, "onAnswer"> & { name: string }> = ({
  part,
  onApproval,
  name,
  steps
}) => {
  // Outside the Case IDE (the agent panel) nothing opens in a tab and there
  // is no workspace to resolve run titles against.
  const ide = useCaseIdeOptional();
  const open = ide?.open;
  const individualId = ide?.individualId ?? "";
  const sections = useCaseDataOptional()?.workspace.sections ?? EMPTY_SECTIONS;
  const titleFor = useTitleFor();
  const approval = "approval" in part ? part.approval : undefined;
  const modeSwitch = name === "request_mode_switch";
  const copy = approvalCopy(name, part.input);
  const declineLabel = modeSwitch ? "Stay" : "Decline";
  const review = useApprovalReview(approval?.id);
  const [reason, setReason] = useState("");
  const [showReason, setShowReason] = useState(false);
  const [allowForThread, setAllowForThread] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [showBody, setShowBody] = useState(false);

  const startedRun = useMemo(
    () =>
      part.state === "output-available"
        ? runFromToolOutput(name, part.output)
        : null,
    [name, part]
  );
  // A system notice about this run (posted when it finished) resolves the card
  // ahead of the tree or the poll below.
  const event = useRunEvent(startedRun);
  const run = useMemo(
    () => (startedRun ? withRunEvent(startedRun, event) : null),
    [startedRun, event]
  );
  // Exports and organizer runs live outside the workspace tree; follow them
  // directly. An organizer run is only followed while the tool output left it
  // in flight — `get_autocategorize_plan`/`apply_*` already report the end state.
  const exportRecord = useExportRun(
    individualId,
    run?.kind === "export" && individualId ? run.id : null
  );
  const autocategorizeRecord = useAutocategorizeRun(
    individualId,
    run && individualId && autocategorizeInFlight(run) ? run.id : null
  );
  const view = run
    ? runView(run, sections, exportRecord, autocategorizeRecord)
    : null;
  const openTarget =
    part.state === "output-available" ? openTargetOf(part.output) : null;
  const handoff = useMemo(
    () =>
      name === "handoff_to_agent" && part.state === "output-available"
        ? handoffFromToolOutput(part.output)
        : null,
    [name, part]
  );
  const runArtifactKind = run ? RUN_KIND_TO_ARTIFACT[run.kind] : undefined;
  const failure = useMemo(
    () =>
      runFailureOf(
        name,
        errorOf(part),
        part.state === "output-available" ? part.output : undefined
      ),
    [name, part]
  );
  const failedDraftId = failure?.draftId ?? null;

  const approvalId = approval?.id;
  const canEdit = review?.allowed_decisions?.includes("edit") ?? false;
  const canAllowForThread = review?.allow_for_thread ?? false;
  const decide = (approved: boolean) => {
    if (!approvalId) return;
    let edited: Record<string, unknown> | undefined;
    if (approved && editing) {
      try {
        const parsed: unknown = JSON.parse(editedText);
        if (!isRecord(parsed)) throw new Error("not an object");
        edited = parsed;
      } catch {
        setEditError("Arguments must be a JSON object.");
        return;
      }
    }
    onApproval({
      id: approvalId,
      approved,
      reason: reason.trim() || undefined,
      edited_args: edited,
      allow_for_thread: approved && canAllowForThread && allowForThread
    });
  };
  const startEditing = () => {
    setEditedText(JSON.stringify(part.input ?? {}, null, 2));
    setEditError(null);
    setEditing(true);
  };
  // Entities the tool returned become a clickable list under the row.
  const entities = useMemo(
    () =>
      part.state === "output-available"
        ? entitiesFromToolOutput(part.output)
        : [],
    [part]
  );
  const rowTitle = useToolTitle(name, part.input, outputOfPart(part));
  // A lone entity that is the call's own target, or that the row already
  // names ("Built an intake 'O-1B ...'"), would say it twice: the row's title
  // links to it instead. A list from a call with no target of its own stays.
  const only = entities.length === 1 ? entities[0] : null;
  const label = only?.label.trim() ?? "";
  const redundant =
    only !== null &&
    ((openTarget !== null &&
      openTarget.kind === only.target.kind &&
      openTarget.id === only.target.id) ||
      (label !== "" && rowTitle.past.includes(label)));
  // A chat attachment saved to Firm Knowledge: its library entry.
  const libraryEntryId =
    name === "save_attachment_to_firm_library" &&
    part.state === "output-available" &&
    isRecord(part.output) &&
    isRecord(part.output.entry) &&
    typeof part.output.entry.id === "string"
      ? part.output.entry.id
      : null;
  // A recorded write says what it wrote, with Undo, on its row: no list too.
  const toolChange = useToolChange(part);
  const organizerMoves =
    run?.kind === "autocategorize" &&
    view?.status === "ready" &&
    individualId ? (
      <OrganizerRunMoves individualId={individualId} runId={run.id} />
    ) : null;
  const showEntities =
    entities.length > 0 &&
    !handoff &&
    !redundant &&
    !libraryEntryId &&
    !toolChange;
  const readNote = useMemo(() => {
    if (part.state !== "output-available") return null;
    if (name !== "read_document") return emptyListNote(name, part.output);
    const out = readDocumentOutputOf(part.output);
    return out ? readNoteText(out) : null;
  }, [name, part]);
  const pdfBatchId =
    part.state === "output-available" && individualId
      ? pdfBatchIdOf(name, part.output)
      : null;
  const timeChanges =
    part.state === "output-available" ? timeEntryChangesOf(part.output) : null;
  const playbookSuggestion =
    part.state === "output-available"
      ? playbookSuggestionOf(part.output)
      : null;
  // Any call the agent delegated and is waiting on shows the delegate's
  // steps under its row (Cursor's Task row), not only deepagents' `task`.
  const liveSteps = Boolean(steps && steps.length > 0);
  const tail =
    Boolean(readNote) ||
    Boolean(approval) ||
    Boolean(handoff) ||
    Boolean(view) ||
    Boolean(failure) ||
    Boolean(libraryEntryId) ||
    Boolean(pdfBatchId) ||
    Boolean(timeChanges) ||
    Boolean(playbookSuggestion);

  return (
    <div className={cn("flex min-w-0 flex-col", tail && "gap-1")}>
      {toolChange ? (
        <ChangeToolRow
          part={part}
          change={toolChange}
          detail={liveSteps && steps ? <StepRows steps={steps} /> : undefined}
          defaultOpen={liveSteps}
          footer={organizerMoves}
        />
      ) : redundant && only && !handoff && !libraryEntryId ? (
        <LinkedToolRow
          part={part}
          entity={only}
          detail={liveSteps && steps ? <StepRows steps={steps} /> : undefined}
          defaultOpen={liveSteps}
        />
      ) : (
        <ToolRow
          part={part}
          doneTitle={
            showEntities ? countedTitle(name, rowTitle, entities) : null
          }
          detail={
            liveSteps && steps ? (
              <StepRows steps={steps} />
            ) : showEntities ? (
              <div className="flex flex-col gap-1.5">
                <ToolResultList entities={entities} />
                {showRawCallDetails(name) ? (
                  <CallDetails
                    input={part.input}
                    output={
                      part.state === "output-available"
                        ? part.output
                        : undefined
                    }
                  />
                ) : null}
              </div>
            ) : undefined
          }
          defaultOpen={liveSteps}
        />
      )}

      {libraryEntryId ? (
        <div className="-ml-1.5">
          <Button
            asChild
            variant="ghost"
            size="xs"
            className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <Link
              to={firmKnowledgePath({ entryId: libraryEntryId })}
              data-testid="library-entry-link"
            >
              Open in library <ExternalLink className="size-3" />
            </Link>
          </Button>
        </div>
      ) : null}

      {readNote ? <ReadNote text={readNote} /> : null}

      {pdfBatchId ? (
        <PdfDraftCard individualId={individualId} batchId={pdfBatchId} />
      ) : null}

      {timeChanges ? <TimeEntryCard changes={timeChanges} /> : null}

      {playbookSuggestion ? (
        <PlaybookSuggestionCard suggestion={playbookSuggestion} />
      ) : null}

      {approval ? (
        <Confirmation
          approval={approval}
          state={part.state}
          className={cn(
            "flex w-auto flex-col gap-0 rounded-none border-0 bg-transparent p-0 text-sm text-fg",
            part.state === "approval-requested" &&
              "overflow-hidden rounded-xl bg-elevated inset-ring inset-ring-line-2"
          )}
          data-testid="approval"
          data-approval-id={approval.id}
        >
          <ConfirmationRequest>
            <div className="flex min-w-0 flex-col gap-2 px-3 pt-2.5 pb-3">
              <WaitingLine approvalId={approval.id} />
              <div className="flex min-w-0 flex-col gap-0.5">
                <ConfirmationTitle
                  className="block text-base font-medium text-fg"
                  data-testid="approval-title"
                >
                  {copy.title}
                </ConfirmationTitle>
                {copy.detail ? (
                  <ConfirmationTitle
                    className="block text-sm text-fg-3"
                    data-testid="approval-detail"
                  >
                    {copy.detail}
                  </ConfirmationTitle>
                ) : null}
              </div>
              {review && !modeSwitch ? (
                <ApprovalFacts
                  review={review}
                  input={part.input}
                  saysNoUndo={copy.saysNoUndo}
                  showBody={showBody}
                  onToggleBody={() => setShowBody((v) => !v)}
                />
              ) : null}
              {editing ? (
                <div className="space-y-1">
                  <Textarea
                    value={editedText}
                    onChange={(e) => {
                      setEditedText(e.target.value);
                      setEditError(null);
                    }}
                    spellCheck={false}
                    className="min-h-24 rounded-md font-mono text-xs"
                    data-testid="approval-edit-args"
                  />
                  {editError ? (
                    <div className="text-xs text-destructive">{editError}</div>
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      The tool runs with these arguments instead.
                    </div>
                  )}
                </div>
              ) : null}
              {showReason ? (
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={`Tell Casey why (optional). On "${declineLabel}" it reads this as what to do instead.`}
                  className="min-h-14 rounded-md text-xs"
                />
              ) : null}
              {canAllowForThread ? (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`allow-${approvalId}`}
                    checked={allowForThread}
                    onCheckedChange={(v) => setAllowForThread(v === true)}
                    className="size-3.5"
                    data-testid="approval-allow-for-thread"
                  />
                  <Label
                    htmlFor={`allow-${approvalId}`}
                    className="text-sm leading-normal font-normal text-fg-2"
                  >
                    {review?.rule === "off_focus" && review.target?.label
                      ? `Don't ask again for ${review.target.label} in this thread`
                      : "Don't ask again in this thread"}
                    <span className="text-fg-4">Only for you</span>
                  </Label>
                </div>
              ) : null}
            </div>
            <ConfirmationActions className="justify-start gap-1 self-stretch border-t border-line-1 py-2 pr-2 pl-1.5">
              {!showReason ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-fg-2"
                  onClick={() => setShowReason(true)}
                >
                  <MessageSquare data-icon="inline-start" />
                  Add Note
                </Button>
              ) : null}
              {canEdit && !editing ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-fg-2"
                  onClick={startEditing}
                  data-testid="approval-edit"
                >
                  <Pencil data-icon="inline-start" />
                  Edit
                </Button>
              ) : null}
              <ConfirmationAction
                variant="secondary"
                size="sm"
                className="ml-auto"
                onClick={() => decide(false)}
              >
                {declineLabel}
              </ConfirmationAction>
              <ConfirmationAction size="sm" onClick={() => decide(true)}>
                {modeSwitch ? "Switch" : editing ? "Approve Edited" : "Approve"}
              </ConfirmationAction>
            </ConfirmationActions>
          </ConfirmationRequest>
          <ConfirmationAccepted>
            <ConfirmationTitle className="flex items-center gap-1.5 text-sm text-fg-3">
              <CheckCircle2 className="size-3.5 shrink-0 text-success" />
              <span className="font-medium text-success">
                {modeSwitch ? "Switched" : "Approved"}
              </span>
              {approval.reason ? <span>— {approval.reason}</span> : null}
            </ConfirmationTitle>
          </ConfirmationAccepted>
          <ConfirmationRejected>
            <ConfirmationTitle className="flex items-center gap-1.5 text-sm text-fg-3">
              <X className="size-3.5 shrink-0" />
              <span className="font-medium">
                {modeSwitch ? "Stayed" : "Declined"}
              </span>
              {approval.reason ? <span>— {approval.reason}</span> : null}
            </ConfirmationTitle>
          </ConfirmationRejected>
        </Confirmation>
      ) : null}

      {handoff ? (
        <HandoffCard handoff={handoff} />
      ) : failure ? (
        <RunFailureCard
          failure={failure}
          onOpen={
            open && failedDraftId
              ? () =>
                  open({
                    kind: "draft",
                    id: failedDraftId,
                    title: titleFor({ kind: "draft", id: failedDraftId })
                  })
              : null
          }
        />
      ) : view ? (
        <RunLine
          view={view}
          onOpen={
            open && runArtifactKind
              ? () =>
                  open({
                    kind: runArtifactKind,
                    id: run!.id,
                    title: titleFor({ kind: runArtifactKind, id: run!.id })
                  })
              : null
          }
          downloadUrl={
            run?.kind === "export" ? exportRecord?.download_url ?? null : null
          }
        />
      ) : null}
      {view &&
      run?.kind === "autocategorize" &&
      view.status === "stale" &&
      individualId ? (
        <OrganizerPlanCard individualId={individualId} runId={run.id} />
      ) : null}
      {toolChange ? null : organizerMoves}
    </div>
  );
};

export const ToolCard: FC<Props> = memo(
  ({ part, onApproval, onAnswer, onConnectResolved, steps }) => {
    const name = toolNameOf(part);
    if (name === "delegate_to_assistant") {
      return <DelegationStep part={part} steps={steps} />;
    }
    if (OWN_CARD_TOOLS.has(name)) {
      return (
        <OwnCard
          name={name}
          part={part}
          onAnswer={onAnswer}
          onConnectResolved={onConnectResolved}
        />
      );
    }
    return (
      <GenericToolCard
        part={part}
        onApproval={onApproval}
        name={name}
        steps={steps}
      />
    );
  }
);
ToolCard.displayName = "ToolCard";
